import { createFileRoute } from "@tanstack/react-router";

import { CopyableValue } from "../components/CopyableValue";
import { Footer } from "../components/Footer";
import { Container } from "../components/ui";
import { t } from "../i18n";

export const Route = createFileRoute("/personvern")({
  component: PrivacyPage,
});

function PrivacyPage() {
  const s = t.privacy;
  // The page renders bare (no AppShell) because /personvern is a public
  // route — see PUBLIC_PREFIXES in __root.tsx. We render the Footer
  // inline so links back to other legal pages stay reachable.
  return (
    <div className="flex min-h-screen flex-col bg-ink-50">
      <Container className="flex-1">
        <article className="prose prose-slate dark:prose-invert max-w-2xl py-8">
          <header className="mb-8">
            <h1 className="mb-3 font-semibold text-3xl text-ink-900">{s.pageTitle}</h1>
            <p className="text-base text-ink-700 leading-relaxed">{s.pageLead}</p>
            <p className="mt-3 text-ink-500 text-xs">{s.lastUpdated}</p>
          </header>

          <Section title={s.sections.controller.title}>
            <p>
              {s.sections.controller.intro}
              <CopyableValue
                value={s.sections.controller.orgNr}
                hint={s.sections.controller.copyHint}
                doneLabel={s.sections.controller.copyDone}
              />
              {s.sections.controller.afterOrgNr}
              <CopyableValue
                value={s.sections.controller.contactEmail}
                hint={s.sections.controller.copyHint}
                doneLabel={s.sections.controller.copyDone}
              />
              {s.sections.controller.outro}
            </p>
          </Section>

          <Section title={s.sections.legalBasis.title}>
            <p>{s.sections.legalBasis.body}</p>
          </Section>

          <Section title={s.sections.whatWeSee.title}>
            <p>{s.sections.whatWeSee.body}</p>
            <ul>
              {s.sections.whatWeSee.weSeeBullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="text-ink-700">{s.sections.whatWeSee.whatStaysVisible}</p>
          </Section>

          <Section title={s.sections.retention.title}>
            <p>{s.sections.retention.body}</p>
          </Section>

          <Section title={s.sections.whyItMatters.title}>
            <p>{s.sections.whyItMatters.body}</p>
          </Section>

          <Section title={s.sections.whoCantRead.title}>
            <ul>
              {s.sections.whoCantRead.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Section>

          <Section title={s.sections.whoCanRead.title}>
            <ul>
              {s.sections.whoCanRead.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Section>

          <Section title={s.sections.memberDeparture.title}>
            <p>{s.sections.memberDeparture.body}</p>
          </Section>

          <Section title={s.sections.helpers.title}>
            <p>{s.sections.helpers.body}</p>
            <ul>
              {s.sections.helpers.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Section>

          <Section title={s.sections.transfers.title}>
            <p>{s.sections.transfers.body}</p>
          </Section>

          <Section title={s.sections.yourRights.title}>
            <p>{s.sections.yourRights.body}</p>
            <p>
              {s.sections.yourRights.contactBefore}
              <CopyableValue
                value={s.sections.yourRights.contactEmail}
                hint={s.sections.yourRights.copyEmailHint}
                doneLabel={s.sections.yourRights.copyEmailDone}
              />
              {s.sections.yourRights.contactAfter}
            </p>
          </Section>

          <Section title={s.sections.complaint.title}>
            <p>{s.sections.complaint.body}</p>
          </Section>

          <Section title={s.sections.honestLimits.title}>
            <p>{s.sections.honestLimits.body}</p>
            <ul>
              {s.sections.honestLimits.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Section>

          <Section title={s.sections.forTechies.title}>
            <p>{s.sections.forTechies.body}</p>
          </Section>
        </article>
      </Container>
      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 font-semibold text-ink-900 text-xl">{title}</h2>
      <div className="space-y-3 text-ink-800 leading-relaxed">{children}</div>
    </section>
  );
}
